/**
 * A **named version of a recipe** — the persisted half of recipe version
 * history (PLAN §3 V1 "recipe versioning/snapshots", PLAN-LAUNCH D3).
 *
 * ## The one idea this document exists for
 *
 * It carries a **frozen copy of the recipe**, not a pointer to it. `recipe` is
 * a whole {@link RecipeDoc}, exactly as {@link BatchDoc.recipeSnapshot} is —
 * this document is the batch snapshot's sibling, generalised from "the recipe I
 * brewed" to "a recipe version I want to keep". Editing the source recipe
 * afterwards cannot move a single number inside a snapshot, which is the whole
 * reason "Brewed as v3" or "Before the assistant's hop rework" means anything.
 *
 * ## Why it lives in the workspace and not next to the command log
 *
 * The Durable Object's command log gives a per-recipe *timeline* for free
 * (`workspace-do.ts`), but it is pruned and it is server-side. A version the
 * user has deliberately named must outlive both: it must survive log retention,
 * work offline, sync to their other devices, and undo like everything else. The
 * way to get all four in this codebase is to be an ordinary document written by
 * ordinary commands — so that is what this is, with the same four-command
 * create/rename/delete/restore shape `waterProfile.*` and `inventory.*` use.
 *
 * `recipeId` is a back-link, like {@link BatchDoc.recipeId}: provenance only,
 * nothing dereferences it to compute anything, and a snapshot of a recipe that
 * has since been deleted is still a complete, restorable recipe.
 */

import { z } from 'zod';
import { RECIPE_SNAPSHOT_SCHEMA_VERSION } from '../version';
import { documentIdSchema, nameSchema, timestampSchema } from './common';
import { type RecipeDoc, recipeDocSchema } from './recipe';

/**
 * How many named snapshots one recipe may hold.
 *
 * A bound rather than "unlimited" for the reason every list schema here has a
 * `.max()`: each snapshot is a whole recipe document, and the workspace is
 * pushed over a socket and mirrored into IndexedDB. Twenty-five is far past
 * what naming *meaningful* versions of one recipe looks like, and the timeline
 * (which is free) covers the rest.
 */
export const RECIPE_SNAPSHOTS_PER_RECIPE = 25;

/** A named, frozen version of one recipe. */
export const recipeSnapshotDocSchema = z.strictObject({
  /** Document schema version; see {@link RECIPE_SNAPSHOT_SCHEMA_VERSION}. */
  schemaVersion: z.number().int().min(1),
  /** Stable snapshot id, unique within the workspace. */
  id: documentIdSchema,
  /**
   * The recipe this is a version of.
   *
   * A back-link for "versions of this recipe" and nothing more; {@link recipe}
   * is what a restore or a diff actually reads.
   */
  recipeId: documentIdSchema,
  /** What the user called this version, e.g. `Brewed as v3`. */
  name: nameSchema,
  /** The recipe **as it was when the snapshot was taken**. Frozen. */
  recipe: recipeDocSchema,
  /**
   * The sync log version this was taken at, or `null` when it was taken from
   * the live recipe rather than from a point on the server timeline.
   *
   * Presentation only: it is what lets the history UI mark a timeline row as
   * named. Never dereferenced — the log is pruned, and this document is
   * complete without it.
   */
  sourceVersion: z.number().int().min(0).nullable(),
  /** Creation time, **ms since epoch**, from the creating command's envelope. */
  createdAt: timestampSchema,
  /** Last-modification time (a rename), **ms since epoch**. */
  updatedAt: timestampSchema,
});

/** @see recipeSnapshotDocSchema */
export type RecipeSnapshotDoc = z.infer<typeof recipeSnapshotDocSchema>;

/**
 * Build the document a `recipeSnapshot.create` command carries.
 *
 * Pure: identity, name, the frozen recipe and the time all come from the
 * caller, exactly as {@link buildBatchDoc} takes its snapshots — this package
 * has no clock and cannot name a version "Today's version" on anyone's behalf.
 * The recipe is copied structurally so a later mutation of the source object by
 * a careless caller cannot reach into the snapshot.
 */
export function buildRecipeSnapshotDoc(args: {
  readonly id: string;
  readonly recipeId: string;
  readonly name: string;
  readonly recipe: RecipeDoc;
  readonly sourceVersion?: number | null;
  readonly timestamp: number;
}): RecipeSnapshotDoc {
  return {
    schemaVersion: RECIPE_SNAPSHOT_SCHEMA_VERSION,
    id: args.id,
    recipeId: args.recipeId,
    name: args.name,
    recipe: { ...args.recipe },
    sourceVersion: args.sourceVersion ?? null,
    createdAt: args.timestamp,
    updatedAt: args.timestamp,
  };
}

/** Every snapshot of one recipe, oldest first. */
export function snapshotsOfRecipe(
  snapshots: Readonly<Record<string, RecipeSnapshotDoc>>,
  recipeId: string,
): readonly RecipeSnapshotDoc[] {
  return Object.values(snapshots)
    .filter((snapshot) => snapshot.recipeId === recipeId)
    .sort((a, b) =>
      a.createdAt === b.createdAt ? a.id.localeCompare(b.id) : a.createdAt - b.createdAt,
    );
}
